import { doc, where, orderBy, limit, runTransaction, deleteField, serverTimestamp } from 'firebase/firestore'
import { FirestoreRepository } from '@upadelmatch/core'
import { db } from '@/core/firebase/client'
import { MATCH_CAPACITY, MatchSchema, type MatchDoc } from '@/core/models/match'
import {
  buildJoinPatch,
  buildLeavePatch,
  joinBlockReason,
  leaveBlockReason,
  newPlaceholderUid,
  buildAddPlaceholderPatch,
  buildRemovePlaceholderPatch,
  buildClaimPatch,
  addPlaceholderBlockReason,
  claimBlockReason,
  type JoinerInfo,
} from '@/core/models/match-membership'
import type { VouchingRecord } from '@/core/models/seat-identity'
import { buildResultPatch, resultBlockReason, type RecordResultInput } from '@/core/models/match-result'

/** Thrown when a join/leave is blocked — carries the pure-logic reason. */
export class MembershipError extends Error {
  constructor(public readonly reason: string) {
    super(reason)
    // ⚠️ WITHOUT THIS LINE THE SENTRY FILTER FOR THIS CLASS CANNOT MATCH.
    // Subclassing Error does NOT set `name` — it stays `'Error'`, inherited
    // from the prototype. Sentry titles an event `${name}: ${message}`, so
    // every membership block arrived as `Error: needs-gender`, and
    // `ignoreErrors: [/^MembershipError/]` in core/sentry.ts — added
    // specifically to drop these — never matched a single one.
    //
    // Measured 2026-08-13 in prod: `Error: needs-gender` sitting in Sentry as
    // an unresolved issue. It is not a bug at all — the app catches that exact
    // reason and opens the gender sheet, which is the designed flow. Every
    // other reason-coded block (`full`, `wrong-gender`, `mixed-side-full`,
    // `already-joined`) was being reported the same way.
    //
    // That noise is the danger, not the volume. This project's Sentry is the
    // place a REAL dropped join is supposed to be visible, and the same file
    // carries a warning about a filter that once "silently ate the very
    // mutation failures we added reporting for". A project full of expected
    // errors is one nobody reads.
    this.name = 'MembershipError'
  }
}

// `matches` — organizer-owned docs (rules: create/update/delete only by the
// organizer; join moves to a CF for atomic seats in ship 2).
class MatchesRepository extends FirestoreRepository<typeof MatchSchema> {
  constructor() {
    super('matches', MatchSchema)
  }

  /**
   * Upcoming matches for a city, soonest first — the discovery feed's base set.
   *
   * ⚠️ `privacy == 'public'` is a CORRECTNESS constraint, not an optimisation.
   * `allow list` now denies any query that could return a private match the
   * caller isn't in, and Firestore fails the WHOLE query when one document is
   * denied — so the query must prove it only asks for public matches. It also
   * means a legacy doc with no `privacy` field can never be fetched here, which
   * matters because the rule's plain field access would throw on one.
   *
   * Needs the composite index `cityKey ASC, privacy ASC, startDateTime ASC`.
   * ⚠️ DEPLOY INDEXES BEFORE THE APP — without it this query throws and the
   * feed renders empty. (Caught by e2e on 2026-08-08 the first time.)
   *
   * status/slots still narrow client-side: small volumes, and neither carries
   * a privacy promise.
   */
  listUpcomingByCity(cityKey: string, now: Date): Promise<MatchDoc[]> {
    return this.list(
      where('cityKey', '==', cityKey),
      where('privacy', '==', 'public'),
      where('startDateTime', '>', now),
      orderBy('startDateTime', 'asc'),
      limit(100),
    )
  }

  create(id: string, payload: Record<string, unknown>): Promise<void> {
    return this.set(id, payload, { setCreatedAt: true })
  }

  /** My matches (organized or joined), any date — split client-side. */
  listMine(uid: string): Promise<MatchDoc[]> {
    return this.list(
      where('players', 'array-contains', uid),
      orderBy('startDateTime', 'asc'),
      limit(100),
    )
  }

  /**
   * Atomic join: the TRANSACTION serializes concurrent joins (the loser
   * re-reads and hits 'full'); the rules validate the same join-shaped diff
   * + gender eligibility server-side. Throws MembershipError on any block.
   */
  async joinMatch(matchId: string, joiner: JoinerInfo): Promise<void> {
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      // No invite lookup any more: "Privado" means unlisted, not invite-only
      // (decision 2026-08-08 — see `joinBlockReason` and the matches rules).
      // This read cost a round-trip inside the transaction on every private
      // join, to enforce a gate that locked out exactly the people the
      // organizer had invited by hand over WhatsApp.
      const block = joinBlockReason(match, joiner)
      if (block) throw new MembershipError(block)
      tx.update(ref, { ...buildJoinPatch(match, joiner), updatedAt: serverTimestamp() })
    })
  }

  async leaveMatch(matchId: string, uid: string): Promise<void> {
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      const block = leaveBlockReason(match, uid)
      if (block) throw new MembershipError(block)
      tx.update(ref, {
        ...buildLeavePatch(match, uid),
        [`playersInfo.${uid}`]: deleteField(),
        updatedAt: serverTimestamp(),
      })
    })
  }

  /** Organizer-only (rules-enforced): cancel notifies via status. */
  cancel(matchId: string): Promise<void> {
    return this.update(matchId, { status: 'cancelled' } as Partial<MatchDoc>)
  }

  /**
   * Companion seat adjust (spec/02 #4, 2026-07-05): the organizer opens a
   * seat when an off-app companion drops (+1) or closes one when a 4th was
   * found outside the app (−1). Transaction: capacity math + the full↔active
   * flip stay atomic vs concurrent joins. Rules: rides the organizer branch.
   */
  /**
   * Seat somebody who is not on the app (spec/02 § Seats & identity).
   *
   * Transactional for the same reason join is: two players adding a companion
   * at once must serialize, or both read `availableSlots: 1` and both write a
   * seat. The loser re-reads and gets `full`.
   */
  async addPlaceholder(
    matchId: string,
    args: { name: string; createdBy: string; gender?: 'male' | 'female' },
  ): Promise<string> {
    const uid = newPlaceholderUid()
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      // The vouching record, read INSIDE the transaction: Firestore requires
      // every read before the first write, and reading it outside would let a
      // player at their limit win a race against their own counter.
      const pubSnap = await tx.get(doc(db, 'players_public', args.createdBy))
      const block = addPlaceholderBlockReason(
        match,
        args.createdBy,
        pubSnap.exists() ? (pubSnap.data() as VouchingRecord) : null,
      )
      if (block) throw new MembershipError(block)
      tx.update(ref, {
        ...buildAddPlaceholderPatch(match, {
          uid,
          name: args.name,
          createdBy: args.createdBy,
          ...(args.gender ? { gender: args.gender } : {}),
        }),
        updatedAt: serverTimestamp(),
      })
    })
    return uid
  }

  /** Only the creator may remove their own seat — enforced again in rules. */
  async removePlaceholder(matchId: string, placeholderUid: string): Promise<void> {
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      tx.update(ref, {
        ...buildRemovePlaceholderPatch(match, placeholderUid),
        updatedAt: serverTimestamp(),
      })
    })
  }

  /**
   * Claim the seat somebody typed your name into.
   *
   * The ONLY write that removes and adds a uid at once, so the transaction
   * matters more than usual: between read and write the seat could have been
   * claimed by someone else, or removed by its creator. `claimBlockReason`
   * re-checks against the fresh snapshot inside the transaction.
   */
  async claimPlaceholder(
    matchId: string,
    args: { placeholderUid: string; claimer: JoinerInfo },
  ): Promise<void> {
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      const block = claimBlockReason(match, args.placeholderUid, args.claimer.uid)
      if (block) throw new MembershipError(block)
      tx.update(ref, {
        ...buildClaimPatch(match, args),
        updatedAt: serverTimestamp(),
      })
    })
  }

  async adjustSeats(matchId: string, delta: 1 | -1): Promise<void> {
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      if (!['active', 'full'].includes(match.status)) throw new MembershipError('not-active')
      const companions = MATCH_CAPACITY - match.players.length - match.availableSlots
      if (delta === 1 && companions <= 0) throw new MembershipError('no-companion-seat')
      if (delta === -1 && match.availableSlots <= 0) throw new MembershipError('full')
      const availableSlots = match.availableSlots + delta
      tx.update(ref, {
        availableSlots,
        status: availableSlots === 0 ? 'full' : 'active',
        updatedAt: serverTimestamp(),
      })
    })
  }

  /**
   * Record the outcome (spec/usersmatch/05): transaction so two players
   * recording simultaneously serialize — the loser re-reads, sees the result
   * and hits 'already-recorded' (unless they're the organizer correcting).
   */
  async recordResult(matchId: string, input: RecordResultInput): Promise<void> {
    await runTransaction(db, async (tx) => {
      const ref = doc(this.collectionRef, matchId)
      const snap = await tx.get(ref)
      if (!snap.exists()) throw new MembershipError('not-found')
      const match = this.parse(snap.id, snap.data())
      if (!match) throw new MembershipError('unreadable')
      const block = resultBlockReason(match, input.recordedBy, input.now)
      if (block) throw new MembershipError(block)
      tx.update(ref, { ...buildResultPatch(match, input), updatedAt: serverTimestamp() })
    })
  }

  newId(): string {
    return doc(this.collectionRef).id
  }
}

export const matchesRepository = new MatchesRepository()
